import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Article, CurrencyDollar, Camera, PawPrint,
  Trash, CreditCard, ArrowLeft, Eye, EyeSlash, SignOut, ToggleLeft, ToggleRight
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { API } from "@/config/constants";

const AdminLoginGate = ({ onAuth }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/auth`, { password });
      onAuth(data.token);
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1d17] flex items-center justify-center p-4" data-testid="admin-login-gate">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#55624F] flex items-center justify-center mx-auto mb-4">
            <ShieldCheck size={32} weight="duotone" className="text-white" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E6E1]">Admin Panel</h1>
          <p className="text-[#7C8068] text-sm font-['Manrope'] mt-1">Nature Journal Administration</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="bg-[#2a2d25] border-[#3a3d35] text-[#E8E6E1] placeholder:text-[#5a5d55] pr-10"
              data-testid="admin-password-input"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7C8068] hover:text-[#E8E6E1]"
            >
              {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#55624F] hover:bg-[#424D3D] text-white"
            data-testid="admin-login-btn"
          >
            {loading ? "Verifying..." : "Access Admin Panel"}
          </Button>
        </form>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = false }) => (
  <Card className="bg-[#2a2d25] border-[#3a3d35]">
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${accent ? "bg-[#C25A3C]/20" : "bg-[#55624F]/20"}`}>
        <Icon size={24} weight="duotone" className={accent ? "text-[#C25A3C]" : "text-[#55624F]"} />
      </div>
      <div>
        <p className="text-[#7C8068] text-xs font-['Manrope'] uppercase tracking-wide">{label}</p>
        <p className="text-[#E8E6E1] text-2xl font-bold font-['Cormorant_Garamond']">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { "X-Admin-Token": token };

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { headers });
      setStats(data);
    } catch { toast.error("Failed to load stats"); }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers });
      setUsers(data);
    } catch { toast.error("Failed to load users"); }
  }, [token]);

  const fetchEntries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/entries`, { headers });
      setEntries(data);
    } catch { toast.error("Failed to load entries"); }
  }, [token]);

  const fetchPayments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/payments`, { headers });
      setPayments(data);
    } catch { toast.error("Failed to load payments"); }
  }, [token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchStats();
      setLoading(false);
    };
    load();
  }, [fetchStats]);

  useEffect(() => {
    if (tab === "users") fetchUsers();
    else if (tab === "entries") fetchEntries();
    else if (tab === "payments") fetchPayments();
  }, [tab, fetchUsers, fetchEntries, fetchPayments]);

  const togglePaid = async (userId) => {
    try {
      const { data } = await axios.put(`${API}/admin/users/${userId}/toggle-paid`, {}, { headers });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, has_paid: data.has_paid } : u));
      toast.success(`User ${data.has_paid ? "granted" : "revoked"} access`);
    } catch { toast.error("Failed to toggle status"); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user "${email}" and ALL their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success("User deleted");
      fetchStats();
    } catch { toast.error("Failed to delete user"); }
  };

  const tabs = [
    { id: "stats", label: "Overview", icon: ShieldCheck },
    { id: "users", label: "Users", icon: Users },
    { id: "entries", label: "Entries", icon: Article },
    { id: "payments", label: "Payments", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-[#1a1d17]" data-testid="admin-dashboard">
      <header className="border-b border-[#3a3d35] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck size={28} weight="duotone" className="text-[#55624F]" />
          <h1 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E6E1]">Admin Panel</h1>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="text-[#7C8068] hover:text-[#E8E6E1] text-sm font-['Manrope'] flex items-center gap-1">
            <ArrowLeft size={16} /> Back to App
          </a>
          <Button variant="ghost" onClick={onLogout} className="text-[#7C8068] hover:text-[#C25A3C]" data-testid="admin-logout-btn">
            <SignOut size={18} />
          </Button>
        </div>
      </header>

      <div className="flex border-b border-[#3a3d35]">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-['Manrope'] flex items-center gap-2 border-b-2 transition-colors ${
              tab === t.id ? "border-[#55624F] text-[#E8E6E1]" : "border-transparent text-[#7C8068] hover:text-[#E8E6E1]"
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            <t.icon size={18} weight={tab === t.id ? "duotone" : "regular"} />
            {t.label}
          </button>
        ))}
      </div>

      <main className="max-w-7xl mx-auto p-6">
        {loading ? (
          <p className="text-[#7C8068] text-center py-12">Loading...</p>
        ) : tab === "stats" && stats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats-grid">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} />
            <StatCard icon={CurrencyDollar} label="Paid Users" value={stats.paid_users} accent />
            <StatCard icon={Article} label="Total Entries" value={stats.total_entries} />
            <StatCard icon={Camera} label="Total Photos" value={stats.total_photos} />
            <StatCard icon={PawPrint} label="Species ID'd" value={stats.total_species} />
            <StatCard icon={CreditCard} label="Payments" value={stats.total_payments} />
            <StatCard icon={CurrencyDollar} label="Revenue" value={`$${stats.total_revenue}`} accent />
            <StatCard icon={Users} label="Free Users" value={stats.free_users} />
          </div>
        ) : tab === "users" ? (
          <div className="space-y-3" data-testid="admin-users-list">
            <p className="text-[#7C8068] text-sm font-['Manrope']">{users.length} users</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#7C8068] border-b border-[#3a3d35]">
                    <th className="pb-3 font-['Manrope'] font-medium">Email</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Name</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Role</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Status</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Entries</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Joined</th>
                    <th className="pb-3 font-['Manrope'] font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-[#2a2d25] hover:bg-[#2a2d25]/50" data-testid={`admin-user-${u.id}`}>
                      <td className="py-3 text-[#E8E6E1] font-['Manrope']">{u.email}</td>
                      <td className="py-3 text-[#E8E6E1] font-['Manrope']">{u.name || "—"}</td>
                      <td className="py-3">
                        <Badge className={u.role === "admin" ? "bg-[#C25A3C]/20 text-[#C25A3C]" : "bg-[#55624F]/20 text-[#55624F]"}>
                          {u.role}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <Badge className={u.has_paid ? "bg-green-900/30 text-green-400" : "bg-yellow-900/30 text-yellow-400"}>
                          {u.has_paid ? "Paid" : "Free"}
                        </Badge>
                      </td>
                      <td className="py-3 text-[#7C8068] font-['Manrope']">{u.entry_count}</td>
                      <td className="py-3 text-[#7C8068] font-['Manrope'] text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => togglePaid(u.id)}
                            className="p-1.5 rounded-lg hover:bg-[#3a3d35] text-[#7C8068] hover:text-[#E8E6E1] transition-colors"
                            title={u.has_paid ? "Revoke access" : "Grant access"}
                            data-testid={`toggle-paid-${u.id}`}
                          >
                            {u.has_paid ? <ToggleRight size={20} weight="fill" className="text-green-400" /> : <ToggleLeft size={20} />}
                          </button>
                          <button
                            onClick={() => deleteUser(u.id, u.email)}
                            className="p-1.5 rounded-lg hover:bg-[#C25A3C]/20 text-[#7C8068] hover:text-[#C25A3C] transition-colors"
                            title="Delete user"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "entries" ? (
          <div className="space-y-3" data-testid="admin-entries-list">
            <p className="text-[#7C8068] text-sm font-['Manrope']">{entries.length} entries (latest 100)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#7C8068] border-b border-[#3a3d35]">
                    <th className="pb-3 font-['Manrope'] font-medium">Title</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Location</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Photos</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Species</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Tags</th>
                    <th className="pb-3 font-['Manrope'] font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e.id} className="border-b border-[#2a2d25] hover:bg-[#2a2d25]/50">
                      <td className="py-3 text-[#E8E6E1] font-['Manrope'] max-w-[200px] truncate">{e.title}</td>
                      <td className="py-3 text-[#7C8068] font-['Manrope'] max-w-[150px] truncate">{e.location || "—"}</td>
                      <td className="py-3 text-[#7C8068] font-['Manrope']">{(e.photos || []).length}</td>
                      <td className="py-3 text-[#7C8068] font-['Manrope']">{(e.species_identified || []).length}</td>
                      <td className="py-3">
                        <div className="flex gap-1 flex-wrap">
                          {(e.tags || []).slice(0, 3).map(t => (
                            <Badge key={t} className="bg-[#55624F]/20 text-[#55624F] text-xs">{t}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 text-[#7C8068] font-['Manrope'] text-xs">
                        {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "payments" ? (
          <div className="space-y-3" data-testid="admin-payments-list">
            <p className="text-[#7C8068] text-sm font-['Manrope']">{payments.length} transactions</p>
            {payments.length === 0 ? (
              <p className="text-[#5a5d55] text-center py-12 font-['Manrope']">No payment transactions yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#7C8068] border-b border-[#3a3d35]">
                      <th className="pb-3 font-['Manrope'] font-medium">Email</th>
                      <th className="pb-3 font-['Manrope'] font-medium">Amount</th>
                      <th className="pb-3 font-['Manrope'] font-medium">Status</th>
                      <th className="pb-3 font-['Manrope'] font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={i} className="border-b border-[#2a2d25] hover:bg-[#2a2d25]/50">
                        <td className="py-3 text-[#E8E6E1] font-['Manrope']">{p.email || "—"}</td>
                        <td className="py-3 text-[#E8E6E1] font-['Manrope']">${p.amount}</td>
                        <td className="py-3">
                          <Badge className={
                            p.payment_status === "completed" ? "bg-green-900/30 text-green-400" :
                            p.payment_status === "initiated" ? "bg-yellow-900/30 text-yellow-400" :
                            "bg-red-900/30 text-red-400"
                          }>
                            {p.payment_status}
                          </Badge>
                        </td>
                        <td className="py-3 text-[#7C8068] font-['Manrope'] text-xs">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token"));

  const handleAuth = (t) => {
    sessionStorage.setItem("admin_token", t);
    setToken(t);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) return <AdminLoginGate onAuth={handleAuth} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
